import { yupResolver } from "@hookform/resolvers/yup";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import api from "../api";
import { button, icon } from "./style";
// Student portal login: username and password only. No role fetch — backend logs you in and returns your role (student → /student/).
const schema = yup.object().shape({
  email: yup.string().required("Username is required").trim(),
  password: yup.string().required("Password is required"),
});

const Login = () => {
  const navigate = useNavigate();

  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "", // sent as "email" to API; backend treats it as login identifier (username)
      password: "",
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("password", data.password);
    // Omit choose_the_role: backend accepts username+password and returns the user's role (student → student portal).

    try {
      setLoginLoading(true);
      const res = await api.post('login', formData);
      const responseData = res.data.data;
      const { access_token, role } = responseData;
      const roleModify = (responseData.portal_role || role?.name || role || "")
        .toString()
        .toLowerCase()
        .trim();
      localStorage.setItem("token", access_token);
      localStorage.setItem("role", roleModify);
      localStorage.setItem('userData', JSON.stringify(responseData));
      if (roleModify === "parent" || roleModify === "student") {
        localStorage.removeItem("admin-token");
      }
      if (roleModify === "parent" && Array.isArray(responseData.permissions)) {
        localStorage.setItem("parent-permissions", JSON.stringify(responseData.permissions));
      } else {
        localStorage.removeItem("parent-permissions");
      }
      if (roleModify === "student" && Array.isArray(responseData.permissions)) {
        localStorage.setItem("student-permissions", JSON.stringify(responseData.permissions));
      } else {
        localStorage.removeItem("student-permissions");
      }
      if (responseData.is_tester && Array.isArray(responseData.tester_permissions)) {
        localStorage.setItem("tester-permissions", JSON.stringify(responseData.tester_permissions));
      } else {
        localStorage.removeItem("tester-permissions");
      }
      if (responseData.must_change_password !== undefined) {
        localStorage.setItem("mustChangePassword", responseData.must_change_password ? "1" : "0");
      }
      // Tester accounts must use the Admin login form (Admin Passport scope required).
      if (roleModify === "tester") {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("userData");
        toast.info("Tester accounts must log in via the Admin login page.");
        navigate("/admin-login");
        return;
      }
      // Clear any leftover admin-token so student/parent API calls use the correct scoped token
      localStorage.removeItem("admin-token");
      toast.success("Login successful!");
      // Always student by default; parent is reached via "Switch to Parent" with PIN from student portal
      const isAdminRole = ["admin", "super admin", "super_admin"].includes(roleModify);
      navigate(isAdminRole ? "/admin/dashboard" : `/${roleModify}/`);

    } catch (error) {
      const errMsg = error.response?.data?.message || "Login failed!";
      toast.error(errMsg);
    } finally {
      setLoginLoading(false);
    }

  };

  return (
    <>
      <Grid container sx={{ height: "100vh" }}>

        {/* Left Section */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundImage: `url("/assets/images/signup-bg.png")`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            // minHeight: { xs: 200, md: "auto" },

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            py: 9
          }}
        >
          <img
            src="/assets/images/hero-right-img.png"
            alt="Character"
            style={{ width: "80%", maxWidth: "300px" }}
          />
        </Grid>

        {/* Right - Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: "#fff",
            py: 6,
            px: { xs: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{ width: "100%", maxWidth: 400 }}
          >
            {/* Logo */}
            <Box sx={{ mb: 4 }}>
              <img
                src="/assets/images/logo.svg"
                alt="Logo"
                style={{ height: "100px", width: "auto" }}
              />
            </Box>

            {/* Heading */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              <Box component="span" sx={{ color: "#D6232A" }}>Login</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Log in with your username. You can switch to the parent portal with your PIN after logging in.
            </Typography>
            {/* Username */}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Username"
                  placeholder="Enter username"
                  variant="outlined"
                  {...field}
                  value={field.value ?? ""}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ mb: 2 }}
                  disabled={loginLoading}
                />
              )}
            />
            {/* Password */}
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  variant="outlined"
                  {...field}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={{ mb: 2 }}
                  disabled={loginLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          sx={{ color: "#666" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              <Link to="/forgot-password" style={{ color: "#1565c0", fontWeight: 600 }}>Forgot password?</Link>
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
              <Button
                disableElevation
                sx={{
                  ...button,
                  backgroundColor: "#EF2A1E",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#EF2A1E",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#EF2A1E",
                    color: "#fff",
                    opacity: 0.7,
                  },
                }}
                onClick={handleSubmit(onSubmit)}
                disabled={loginLoading}
              >
                {loginLoading ? "Logging in..." : "Login"}
                {!loginLoading && (
                  <Box sx={icon}>
                    <ArrowForwardIcon sx={{ fontSize: 20, color: "#EF2A1E" }} />
                  </Box>
                )}
              </Button>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Are you a tutor?{" "}
                <Link to="/tutor-login" style={{ color: "#1565c0", fontWeight: 600 }}>Log in to the tutor panel</Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default Login;
